import qs from 'qs';
import config from '../../config';

// getLocation patch cause IE doesn't support `new URL`
// https://caniuse.com/#search=URL
export function getLocation(href) {
  const match = href.match(/^(https?:)\/\/(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
  return match && {
    href,
    protocol: match[1],
    host: match[2],
    hostname: match[3],
    port: match[4],
    pathname: match[5],
    search: match[6],
    hash: match[7],
  };
}

export function getQuery() {
  function get(a) {
    if (a === '') return {};
    const b = {};
    a.forEach((ai) => {
      const p = ai.split('=', 2);
      if (p.length === 1) {
        b[p[0]] = '';
      } else {
        b[p[0]] = decodeURIComponent(p[1].replace(/\+/g, ' '));
      }
    });
    return b;
  }
  const query = get(window.location.search.substr(1).split('&'));
  return query;
}

// qs only reads an indexed list (`a[0]=x&a[1]=y`) back as an array up to `arrayLimit` entries
// (default 20). Above that it returns an object keyed "0", "1", ..., which broke every filter that
// expects an array: ticking a 22nd agency on Edit Units blanked the page (CPI-1444). Filters stored
// in the URL can legitimately hold many values, so read them back as arrays at any realistic size.
export const SEARCH_ARRAY_LIMIT = 1000;
const SEARCH_PARSE_OPTIONS = { arrayLimit: SEARCH_ARRAY_LIMIT };

export function decodeSearch(search) {
  if (search.startsWith('?_q=')) {
    return qs.parse(atob(search.substring(4)), SEARCH_PARSE_OPTIONS);
  }
  return qs.parse(search.substring(1), SEARCH_PARSE_OPTIONS);
}

export function encodeSearch(query, hide = false) {
  if (hide) {
    return `_q=${btoa(qs.stringify(query))}`;
  }
  return qs.stringify(query);
}

// Local dev: when AWS_S3_ENDPOINT is set (e.g. https://s3-local.mhub.my -> Caddy -> MinIO), object URLs
// are built path-style against it instead of the AWS S3 host. Returns null in real envs (var
// unset),
// so the AWS URL paths below stay byte-identical in production.
function localS3Base() {
  const ep = process.env.AWS_S3_ENDPOINT;
  if (!ep || ep === 'undefined') return null;
  // Normalize: trim whitespace + strip trailing slash(es) so the `${base}/${bucket}/...` joins
  // below
  // never produce double slashes from a sloppily-set env var. Prod leaves the var unset -> null.
  const base = ep.trim().replace(/\/+$/, '');
  return base !== '' ? base : null;
}

export function siteplanUrl(appConfig, fileUrl) {
  const staticRegion = config.get('s3SiteplanRegion');
  const envRegion = process.env.AWS_SITEPLAN_REGION;
  const envBucket = process.env.AWS_SITEPLAN_BUCKET;
  let region = staticRegion;
  let bucket = '';
  if (envRegion !== 'undefined') {
    region = envRegion;
  } else if (appConfig && appConfig.data && appConfig.data.webAwsSiteplanRegion) {
    region = appConfig.data.webAwsSiteplanRegion;
  }
  if (envBucket !== 'undefined') {
    bucket = envBucket;
  } else if (appConfig && appConfig.data && appConfig.data.webAwsSiteplanBucket) {
    bucket = appConfig.data.webAwsSiteplanBucket;
  }
  const lb = localS3Base();
  if (lb && bucket) return `${lb}/${bucket}/${fileUrl}`;
  if (region && bucket) return `https://s3-${region}.amazonaws.com/${bucket}/${fileUrl}`;
  return '';
}

export function AWSUrl(appConfig, fileUrl, bucketName = null) {
  const staticRegion = config.get('s3Region');
  const envRegion = process.env.AWS_REGION;
  const envBucket = bucketName || process.env.AWS_BUCKET;
  const lb = localS3Base();
  if (lb) {
    const b = envBucket || (appConfig && appConfig.data && appConfig.data.webAwsBucket);
    if (b) return `${lb}/${b}/${fileUrl}`;
  }
  if (bucketName) {
    return `https://${envBucket}.s3-${envRegion}.amazonaws.com/${fileUrl}`;
  }
  if (envRegion && envBucket) {
    return `https://s3-${envRegion}.amazonaws.com/${envBucket}/${fileUrl}`;
  } else if (appConfig && appConfig.data &&
    appConfig.data.webAwsRegion && appConfig.data.webAwsBucket) {
    return `https://s3-${appConfig.data.webAwsRegion}.amazonaws.com/${appConfig.data.webAwsBucket}/${fileUrl}`;
  }
  if (envBucket) {
    return `https://s3-${staticRegion}.amazonaws.com/${envBucket}/${fileUrl}`;
  } else if (appConfig && appConfig.data && appConfig.data.webAwsBucket) {
    return `https://s3-${staticRegion}.amazonaws.com/${appConfig.data.webAwsBucket}/${fileUrl}`;
  }
  return '';
}

export function parseParams(url, query = {}, hash = {}) {
  let parsedURL = url;
  if (query && Object.keys(query).length > 0) {
    parsedURL += `?${qs.stringify(query)}`;
  }
  if (hash && Object.keys(hash).length > 0) {
    parsedURL += `#${qs.stringify(hash)}`;
  }
  return parsedURL;
}

export function cleanURL(paramToRemove) {
  // Use IE-compatible getLocation instead of `new URL`
  const location = getLocation(window.location.href);
  if (!location) {
    // Fallback for environments where getLocation might fail
    const url = new URL(window.location.href);
    try {
      url.searchParams.delete(paramToRemove);
    } catch (e) {
      // If both getLocation and URL fail, do nothing
    }
    return `${url.pathname}${url.search}${url.hash}`;
  }
  let search = location.search || '';
  if (search.startsWith('?')) {
    search = search.substring(1);
  }
  const params = qs.parse(search);
  if (Object.prototype.hasOwnProperty.call(params, paramToRemove)) {
    delete params[paramToRemove];
  }
  const newSearch = qs.stringify(params);
  const searchPart = newSearch ? `?${newSearch}` : '';
  const pathname = location.pathname || '';
  const hash = location.hash || '';
  return `${pathname}${searchPart}${hash}`;
}

export const sanitizeUrl = (input) => {
  if (!input) return null;

  const trimmed = String(input).trim();
  const location = getLocation(trimmed);
  if (!location) return null;

  if (!['http:', 'https:'].includes(location.protocol)) {
    return null;
  }
  return trimmed;
};



// WEBPACK FOOTER //
// ./src/utils/url.js